import { select, Selection } from 'd3-selection'
import { css } from 'emotion'

const tooltipCss = css`
    z-index: 100000000;
    text-align: left;
    white-space: nowrap;
    padding: 2px 4px;
    box-shadow: 0px 2px 10px -1px rgba(0, 0, 0, 0.24);
    position: absolute;
    background: #f1f1f1;
    border: 1px solid #a1a1a1;

    h3 {
        font-size: 14px;
        font-weight: bold;
    }

    span.value {
        float: right;
        margin-left: 10px;
        font-weight: bold;
    }
    span.label {
        vertical-align: top;
    }
    .item {
        margin: 2px 1px;
    }

    i {
        vertical-align: middle;
        display: inline-block;
        width: 18px;
        height: 18px;
        border-radius: 2px;
        margin-right: 4px;
        box-shadow: inset 0 0 0 1px hsla(220, 40%, 35%, 0.2);
    }
`

export default class Tooltip<T> {
    protected el: Selection<HTMLElement, unknown, null, undefined>
    private offset: { top: number; left: number }

    constructor() {
        this.init()
    }

    init(): void {
        this.el = select(document.body).append('div').classed(tooltipCss, true)
    }

    getEl(): Selection<HTMLElement, unknown, null, undefined> {
        return this.el
    }

    render(
        items: { color?: string; src?: string; description: string; value: string | number }[],
        title: string | null,
        mouse: [number, number]
    ): void {
        this.show()
        this.position(mouse[0], mouse[1])

        if (title) {
            this.el
                .selectAll('h3')
                .data([title])
                .join('h3')
                .text(d => d)
        }

        this.el
            .selectAll('div.item')
            .data(items)
            .join('div')
            .classed('item', true)
            .call(selection => {
                selection
                    .selectAll('i')
                    .data(d => [d])
                    .join('i')
                    .style('display', d => (d.color ? null : 'none'))
                    .style('background-color', d => d.color)
                    .style('background-image', d => (d.src ? `url(${d.src})` : ''))

                selection
                    .selectAll('span.label')
                    .data(d => [d])
                    .join('span')
                    .classed('label', true)
                    .text(d => d.description)

                selection
                    .selectAll('span.value')
                    .data(d => [d])
                    .join('span')
                    .classed('value', true)
                    .text(d => d.value)
            })
    }

    setOffset(offset: { top: number; left: number }): void {
        this.offset = offset
    }

    show(): void {
        this.el.style('display', '')
    }

    position(mouseX: number, mouseY: number): void {
        const xAdjust = 16 + this.offset.left
        const overflowX = mouseX > window.innerWidth - (this.innerWidth + xAdjust)
        const overflowY = mouseY > window.innerHeight - (this.innerHeight + this.offset.top)

        this.el
            .style('top', overflowY ? null : mouseY + this.offset.top + 'px')
            .style('left', overflowX ? null : mouseX + xAdjust + 'px')
            .style('bottom', overflowY ? window.innerHeight - mouseY + this.offset.top + 'px' : null)
            .style('right', overflowX ? window.innerWidth - mouseX + xAdjust + 'px' : null)
    }

    get innerWidth(): number {
        return this.el.node().getBoundingClientRect().width
    }

    get innerHeight(): number {
        return this.el.node().getBoundingClientRect().height
    }

    hide(): void {
        this.el.style('display', 'none')
    }

    destroy(): void {
        this.el.remove()
    }
}
